/**
 * EnumMappingEditor — Inline from/to value mapping table.
 * Kept minimal: just the entries grid, an "Add Row" button, and a fallback row.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { EnumMapping, EnumMappingEntry } from '../../types/outputFormats';
import './OutputFormats.css';

interface EnumMappingEditorProps {
  mapping: EnumMapping;
  onChange: (updated: EnumMapping) => void;
  onClose: () => void;
}

const EnumMappingEditor: React.FC<EnumMappingEditorProps> = ({
  mapping,
  onChange,
}) => {
  const { t } = useTranslation();

  const updateEntry = (idx: number, updates: Partial<EnumMappingEntry>) => {
    const entries = mapping.entries.map((e, i) =>
      i === idx ? { ...e, ...updates } : e
    );
    onChange({ ...mapping, entries });
  };

  const addEntry = () => {
    onChange({
      ...mapping,
      entries: [...mapping.entries, { from: '', to: '' }],
    });
  };

  const removeEntry = (idx: number) => {
    onChange({
      ...mapping,
      entries: mapping.entries.filter((_, i) => i !== idx),
    });
  };

  const isBlankFallback = mapping.fallback === '';
  const hasCustomFallback = mapping.fallback !== null && mapping.fallback !== '';

  const updateFallback = (value: string) => {
    onChange({ ...mapping, fallback: value || null });
  };

  // Check for duplicate "from" values
  const fromCounts = mapping.entries.reduce<Record<string, number>>((acc, e) => {
    if (e.from) acc[e.from] = (acc[e.from] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="enum-mapping-editor">
      <table className="enum-entries-table">
        <thead>
          <tr>
            <th>{t('outputFormats.enumMapping.from')}</th>
            <th>{t('outputFormats.enumMapping.to')}</th>
            <th style={{ width: '30px' }}></th>
          </tr>
        </thead>
        <tbody>
          {mapping.entries.map((entry, idx) => (
            <tr key={idx} className={fromCounts[entry.from] > 1 ? 'duplicate-warning' : ''}>
              <td>
                <input
                  type="text"
                  value={entry.from}
                  onChange={(e) => updateEntry(idx, { from: e.target.value })}
                />
              </td>
              <td>
                <input
                  type="text"
                  value={entry.to}
                  onChange={(e) => updateEntry(idx, { to: e.target.value })}
                />
              </td>
              <td>
                <button
                  className="btn-icon btn-danger-icon"
                  onClick={() => removeEntry(idx)}
                  title={t('outputFormats.tooltips.remove')}
                  type="button"
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
          {/* Fallback / "Others" row */}
          <tr className="enum-fallback-row">
            <td>
              <span className="enum-fallback-label">
                {t('outputFormats.enumMapping.othersLabel')}
              </span>
            </td>
            <td>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isBlankFallback}
                  onChange={(e) => {
                    onChange({ ...mapping, fallback: e.target.checked ? '' : null });
                  }}
                />
                {t('outputFormats.enumMapping.blankForUnmapped')}
              </label>
              {!isBlankFallback && (
                <input
                  type="text"
                  value={hasCustomFallback ? mapping.fallback! : ''}
                  onChange={(e) => updateFallback(e.target.value)}
                  placeholder={t('outputFormats.enumMapping.othersPlaceholder')}
                  style={{ marginTop: '4px' }}
                />
              )}
            </td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <button className="btn-small btn-secondary" onClick={addEntry} type="button">
        + {t('outputFormats.enumMapping.addEntry')}
      </button>
    </div>
  );
};

export default EnumMappingEditor;
